/**
 * Cart Drawer — Winning Formula Implementation
 * =============================================================================
 *
 * CartDrawerController — Handles all AJAX cart operations, shipping bar updates,
 * cross-sell interactions, and dynamic section re-rendering.
 * Works alongside the existing CartDrawer & CartDrawerItems web components.
 *
 * =============================================================================
 */

class CartDrawerController {
    constructor() {
      this.drawer = document.getElementById('CartDrawer');
      if (!this.drawer) return;
  
      this.shippingBar = document.getElementById('cart-drawer-shipping-bar');
      this.isUpdating = false;
      this.isNavigating = false;  // guard: pauses re-renders during checkout nav
  
      this.bindEvents();
  
      // Run shipping bar calculation on initial page load
      this.updateShippingBar();
  
      // Listen for our custom cart:updated event
      document.addEventListener('cart:updated', () => {
        if (this.isNavigating) return;
        this.rerenderSection();
        this.updateShippingBar();
      });
  
      // Handle browser back button (bfcache restore) —
      // Reset navigating guard and force a full re-render so the drawer
      // shows the actual cart state instead of stale cached DOM.
      window.addEventListener('pageshow', (event) => {
        this.isNavigating = false;
        if (event.persisted) {
          // Page was restored from bfcache — DOM is stale
          this.rerenderSection();
          this.updateShippingBar();
        }
      });
  
      // Hook into Dawn's native renderContents() —
      // Observe ONLY direct children of CartDrawer (not subtree).
      // Dawn's renderContents() replaces .drawer__inner as a direct child,
      // so childList on the direct parent is sufficient and avoids firing
      // on class/attribute changes that happen during drawer open/close animations.
      let _observerTimer = null;
      this._observer = new MutationObserver((mutations) => {
        if (this.isNavigating) return;
  
        // Only care about nodes being added (Dawn replacing section HTML)
        const hasAddedNodes = mutations.some(m => m.addedNodes.length > 0);
        if (!hasAddedNodes) return;
  
        // Debounce — wait for DOM to settle before re-binding/updating
        clearTimeout(_observerTimer);
        _observerTimer = setTimeout(() => {
          this.shippingBar = document.getElementById('cart-drawer-shipping-bar');
          this.bindEvents();
          this.updateShippingBar();
        }, 100);
      });
  
      // childList only on the direct CartDrawer div — NOT subtree
      this._observer.observe(this.drawer, { childList: true, subtree: false });
    }
  
    /**
     * Bind all event listeners via delegation on the drawer container.
     * Called once on init and again after each DOM re-render.
     */
    bindEvents() {
      const drawer = this.drawer;
      if (!drawer) return;
  
      // Remove old listeners by replacing the event-delegation root
      // We use a single click handler via delegation to avoid duplicate bindings
      if (this._clickHandler) {
        drawer.removeEventListener('click', this._clickHandler);
      }
      if (this._changeHandler) {
        drawer.removeEventListener('change', this._changeHandler);
      }
  
      // --- Click delegation ---
      this._clickHandler = (e) => {
        // Checkout button — set navigating guard and let the form submit freely
        const checkoutBtn = e.target.closest('#cart-drawer-checkout-btn');
        if (checkoutBtn) {
          this.isNavigating = true;
          return; // do NOT preventDefault — let the form submit naturally
        }
  
        // Close button
        const closeBtn = e.target.closest('#cart-drawer-close-btn');
        if (closeBtn) {
          e.preventDefault();
          const cartDrawerEl = closeBtn.closest('cart-drawer');
          if (cartDrawerEl && typeof cartDrawerEl.close === 'function') {
            cartDrawerEl.close();
          }
          return;
        }
  
        // Quantity buttons
        const qtyBtn = e.target.closest('.cart-drawer__qty-btn');
        if (qtyBtn) {
          e.preventDefault();
          const line = parseInt(qtyBtn.dataset.line, 10);
          const action = qtyBtn.dataset.action;
          const qtyGroup = qtyBtn.closest('.cart-drawer__qty');
          const input = qtyGroup ? qtyGroup.querySelector('.cart-drawer__qty-input') : null;
          if (!input) return;

          let currentQty = parseInt(input.value, 10) || 0;

          if (action === 'decrease') {
            currentQty = Math.max(0, currentQty - 1);
          } else if (action === 'increase') {
            currentQty += 1;
          }

          input.value = currentQty;
          this.debouncedUpdateCart(line, currentQty);
          return;
        }
  
        // Remove button
        const removeBtn = e.target.closest('.cart-drawer__item-remove');
        if (removeBtn) {
          e.preventDefault();
          const line = parseInt(removeBtn.dataset.line, 10);
          const item = removeBtn.closest('.cart-drawer__item');
          if (item) item.classList.add('is-removing');
          this.updateCart(line, 0);
          return;
        }
  
        // Cross-sell / empty-state add button
        const addBtn = e.target.closest('.cart-drawer__crosssell-add');
        if (addBtn) {
          e.preventDefault();
          const variantId = addBtn.dataset.variantId;
          if (variantId) this.addToCart(variantId, addBtn);
          return;
        }
      };
  
      // --- Change delegation (qty input direct edit) ---
      this._changeHandler = (e) => {
        if (e.target.matches('.cart-drawer__qty-input')) {
          const line = parseInt(e.target.dataset.line, 10);
          let qty = parseInt(e.target.value, 10);
          if (isNaN(qty) || qty < 0) qty = 0;
          e.target.value = qty;
          this.debouncedUpdateCart(line, qty);
        }
      };
  
      drawer.addEventListener('click', this._clickHandler);
      drawer.addEventListener('change', this._changeHandler);
    }
  
    /**
     * Debounced wrapper for updateCart.
     * Buffers rapid quantity clicks (e.g. clicking + multiple times fast)
     * and only sends the final value after 350ms of inactivity.
     * @param {number} line - 1-indexed line number
     * @param {number} quantity - new quantity (0 = remove)
     */
    debouncedUpdateCart(line, quantity) {
      clearTimeout(this._debouncedTimers?.[line]);
      if (!this._debouncedTimers) this._debouncedTimers = {};
      this._debouncedTimers[line] = setTimeout(() => {
        this.updateCart(line, quantity);
      }, 350);
    }

    /**
     * Update a cart line item quantity via Shopify Cart API.
     * Disables quantity controls during the request to prevent overlapping calls.
     * @param {number} line - 1-indexed line number
     * @param {number} quantity - new quantity (0 = remove)
     */
    async updateCart(line, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;
      this._setQtyLoadingState(true);

      try {
        const response = await fetch(
          `${this._routesRoot()}cart/change.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ line, quantity })
          }
        );

        if (!response.ok) {
          throw new Error(`Cart update failed: ${response.status}`);
        }

        const cart = await response.json();
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart, source: 'cart-drawer' } })
        );
      } catch (error) {
        console.error('[CartDrawer] updateCart error:', error);
      } finally {
        this.isUpdating = false;
        this._setQtyLoadingState(false);
      }
    }

    /**
     * Enable or disable all quantity controls in the drawer.
     * Adds a CSS class for visual feedback (opacity/pointer-events).
     * @param {boolean} loading
     */
    _setQtyLoadingState(loading) {
      const groups = this.drawer.querySelectorAll('.cart-drawer__qty');
      groups.forEach(group => {
        const btns = group.querySelectorAll('.cart-drawer__qty-btn');
        const input = group.querySelector('.cart-drawer__qty-input');
        btns.forEach(btn => { btn.disabled = loading; });
        if (input) input.disabled = loading;
        group.classList.toggle('cart-drawer__qty--updating', loading);
      });
    }

    /**
     * Add a variant to the cart via Shopify Cart API.
     * Supports ReCharge selling plans — if the button has a data-selling-plan
     * attribute, the selling_plan ID is included in the request payload.
     * @param {string|number} variantId
     * @param {HTMLButtonElement} button - the clicked button (for UI feedback)
     */
    async addToCart(variantId, button) {
      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
  
      try {
        // Build payload — include selling_plan if present (ReCharge subscriptions)
        const payload = {
          id: parseInt(variantId, 10),
          quantity: 1
        };
  
        const sellingPlan = button.dataset.sellingPlan;
        if (sellingPlan) {
          payload.selling_plan = parseInt(sellingPlan, 10);
        }
  
        const response = await fetch(
          `${this._routesRoot()}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          }
        );
  
        if (!response.ok) {
          throw new Error(`Add to cart failed: ${response.status}`);
        }
  
        // Success feedback
        button.textContent = '✓ Added';
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { source: 'cart-drawer-crosssell' } })
        );
  
        // Restore button after delay
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
  
      } catch (error) {
        console.error('[CartDrawer] addToCart error:', error);
        button.textContent = originalText;
        button.disabled = false;
      }
    }
  
    /**
     * Re-render cart drawer contents via Shopify Section Rendering API.
     * Only replaces inner DOM nodes to preserve outer event listeners.
     */
    async rerenderSection() {
      try {
        const response = await fetch(
          `${this._routesRoot()}?sections=cart-drawer`
        );
        if (!response.ok) return;
  
        const json = await response.json();
        const parser = new DOMParser();
        const doc = parser.parseFromString(json['cart-drawer'], 'text/html');
  
        // Replace cart items
        const newItems = doc.getElementById('cart-drawer-items');
        const currentItems = document.getElementById('cart-drawer-items');
        if (newItems && currentItems) {
          currentItems.innerHTML = newItems.innerHTML;
        }
  
        // Replace cross-sell block
        const newCrosssell = doc.getElementById('cart-drawer-crosssell');
        const currentCrosssell = document.getElementById('cart-drawer-crosssell');
        if (currentCrosssell && newCrosssell) {
          currentCrosssell.replaceWith(newCrosssell);
        } else if (currentCrosssell && !newCrosssell) {
          currentCrosssell.remove();
        } else if (!currentCrosssell && newCrosssell) {
          // Cross-sell appeared (new items qualify) — append to scrollable
          const scrollable = document.getElementById('cart-drawer-scrollable');
          if (scrollable) scrollable.appendChild(newCrosssell);
        }
  
        // Replace footer (handles empty ↔ non-empty transitions)
        const newFooter = doc.getElementById('cart-drawer-footer');
        const currentFooter = document.getElementById('cart-drawer-footer');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        }
  
        // Update subtotal text
        const newSubtotal = doc.getElementById('cart-drawer-subtotal');
        const currentSubtotal = document.getElementById('cart-drawer-subtotal');
        if (newSubtotal && currentSubtotal) {
          currentSubtotal.textContent = newSubtotal.textContent;
        }
  
        // Update item count badge
        const newCount = doc.querySelector('.cart-drawer__count');
        const currentCount = document.querySelector('.cart-drawer__count');
        if (newCount && currentCount) {
          currentCount.textContent = newCount.textContent;
        }
  
        // Also update the header cart icon bubble (outside the drawer)
        this._updateCartIconBubble();
  
      } catch (error) {
        console.error('[CartDrawer] rerenderSection error:', error);
      }
    }
  
    /**
     * Dynamically update the shipping bar without a full section re-render.
     * Fetches current cart total from /cart.js and recalculates progress.
     */
    async updateShippingBar() {
      const bar = this.shippingBar || document.getElementById('cart-drawer-shipping-bar');
      if (!bar) return;
  
      try {
        const response = await fetch(`${this._routesRoot()}cart.js`, {
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) return;
  
        const cart = await response.json();
        const threshold = parseInt(bar.dataset.threshold, 10);
        const cartTotal = cart.total_price;
        const remaining = threshold - cartTotal;
        const isUnlocked = remaining <= 0;
        const progressPct = isUnlocked
          ? 100
          : Math.min(100, Math.round((cartTotal / threshold) * 100));
  
        // Update data attribute
        bar.dataset.cartTotal = cartTotal;
  
        // Animate the fill bar
        const fill = document.getElementById('cart-drawer-shipping-fill');
        if (fill) {
          fill.style.width = `${progressPct}%`;
          if (isUnlocked) {
            fill.classList.add('cart-drawer__shipping-fill--complete');
          } else {
            fill.classList.remove('cart-drawer__shipping-fill--complete');
          }
        }
  
        // Update the text message
        const textEl = bar.querySelector('.cart-drawer__shipping-text');
        if (textEl) {
          if (isUnlocked) {
            textEl.innerHTML =
              '<span class="cart-drawer__shipping-text--success">✓ You\'ve unlocked free shipping!</span>';
          } else {
            const remainingFormatted = this._formatMoney(remaining);
            textEl.textContent = `Add ${remainingFormatted} more for free shipping`;
          }
        }
      } catch (error) {
        console.error('[CartDrawer] updateShippingBar error:', error);
      }
    }
  
    /**
     * Update the header cart icon bubble count (outside the drawer).
     */
    async _updateCartIconBubble() {
      try {
        const response = await fetch(
          `${this._routesRoot()}?sections=cart-icon-bubble`
        );
        if (!response.ok) return;
  
        const json = await response.json();
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && json['cart-icon-bubble']) {
          const doc = new DOMParser().parseFromString(json['cart-icon-bubble'], 'text/html');
          const newBubble = doc.querySelector('.shopify-section');
          if (newBubble) {
            bubble.innerHTML = newBubble.innerHTML;
          }
        }
      } catch (error) {
        // Non-critical — silently ignore
      }
    }
  
    /**
     * Format a money value (in cents) as a currency string.
     * @param {number} cents
     * @returns {string}
     */
    _formatMoney(cents) {
      const amount = (Math.abs(cents) / 100).toFixed(2);
      const currency = window.Shopify?.currency?.active || 'USD';
  
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency
        }).format(Math.abs(cents) / 100);
      } catch (e) {
        return `$${amount}`;
      }
    }
  
    /**
     * Get the Shopify routes root (handles multi-currency/locale prefixes).
     * @returns {string}
     */
    _routesRoot() {
      return window.Shopify?.routes?.root || '/';
    }
  
    /**
     * Static initializer — called on DOMContentLoaded.
     */
    static init() {
      new CartDrawerController();
    }
  }
  
  // Initialize CartDrawerController when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', CartDrawerController.init);
  } else {
    CartDrawerController.init();
  }
  
  
  /* ==========================================================================
     Existing Web Components (preserved for theme compatibility)
     ========================================================================== */
  
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
  
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
    }
  
    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;
  
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  
    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });
  
      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close') || this.querySelector('.cart-drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );
  
      document.body.classList.add('overflow-hidden');
    }
  
    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }
  
    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');
  
      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }
  
      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });
  
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  
    renderContents(parsedState) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
  
        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
  
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    }
  
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }
  
    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }
  
    setActiveElement(element) {
      this.activeElement = element;
    }
  }
  
  customElements.define('cart-drawer', CartDrawer);
  
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }
  
  customElements.define('cart-drawer-items', CartDrawerItems);
  